import styles from "../css/WrongAnswerNote.module.css";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import AudioPlayer from "react-h5-audio-player";
import { delay } from "lodash";
import classNames from "classnames";
import Term from "../../term/Term";
import SpeakButton from "../../common/SpeakButton";
import MarkButton from "../../common/MarkButton";
import PronunciationSelectView from "../../common/view/PronunciationSelectView";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import {
  EnglishPronunciation,
  getVoiceFile,
  makeBase64String,
} from "../../voice/util/voice";
import {
  isHiraganaOnly,
  sanitizeAndSplitStringAndNormalize,
  sanitizeStringAndNormalize,
} from "../../common/strings";
import { isOnLearningTextToSpeechFromLocalStorage } from "../../settings/util/learningSettings";
import { useReadingSettings } from "../../common/util/chineseCharacterMode";
import { formatShortcut } from "../../common/util/shourtcut";
import Button from "../../../components/ui/Button";
import TextField from "../../../components/ui/TextField";

interface WrongAnswerNoteProps {
  term: Term;
  userAnswer: string;
  englishPronunciation: EnglishPronunciation;
  showingPronunciationSelectView: boolean;
  onClickMarkButton: (term: Term) => void;
  onClickPronunciationSelectButton: (
    pronunciation: EnglishPronunciation
  ) => void;
  onSpeakError: (error: string) => void;
  // 정답을 따라 적었거나 그냥 넘어갔을 때 호출된다
  onFinish: () => void;
}

// 단어 또는 뜻 중 하나를 그대로 따라 적으면 정답으로 본다 (iOS LearningIncorrectVM 과 동일한 기준)
function isCorrectCopy(input: string, term: Term): boolean {
  const userFullString = sanitizeStringAndNormalize(input);
  if (userFullString.length === 0) return false;

  const candidates = [term.term, term.definition];
  if (
    candidates.some((value) => sanitizeStringAndNormalize(value) === userFullString)
  ) {
    return true;
  }

  const userInputs = sanitizeAndSplitStringAndNormalize(input);
  if (userInputs.length === 0) return false;
  return candidates.some((value) => {
    const answerSet = new Set(sanitizeAndSplitStringAndNormalize(value));
    return userInputs.every((input) => answerSet.has(input));
  });
}

export default function WrongAnswerNote(props: WrongAnswerNoteProps) {
  const { t } = useTranslation();
  const { chineseCharacterMode } = useReadingSettings();

  const [input, setInput] = useState("");
  const [isError, setIsError] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string>();
  const [isPlaying, setIsPlaying] = useState(false);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const speechShourtCut = formatShortcut({ mac: "⌘ S", win: "Ctrl + S" });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s";

      if (!isSave) return;

      e.preventDefault();
      speak();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOnClickMarkButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.onClickMarkButton(props.term);
  }

  function handleOnClickSpeakButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    speak();
  }

  function handleOnSubmit() {
    if (isCompleted || input.trim().length === 0) return;

    if (!isCorrectCopy(input, props.term)) {
      setIsError(true);
      return;
    }

    setIsError(false);
    setIsCompleted(true);
    if (isOnLearningTextToSpeechFromLocalStorage()) {
      speak();
    }
    delay(() => {
      props.onFinish();
    }, 1000);
  }

  function handleOnClickPassButton() {
    if (isCompleted) return;
    props.onFinish();
  }

  async function speak() {
    setIsPlaying(true);
    setAudioSrc(undefined);
    try {
      const languageCode =
        props.term.group.length !== 0
          ? await fetchGroupLanguageCode(props.term.group)
          : "";

      const text = isHiraganaOnly(props.term.pronunciation)
        ? props.term.pronunciation
        : props.term.term;

      const audio = await getVoiceFile(text, languageCode);
      setAudioSrc(makeBase64String(audio));
    } catch (error) {
      setIsPlaying(false);
      if (error instanceof Error) {
        props.onSpeakError(error.message);
      }
    }
  }

  return (
    <motion.div
      className={styles.containerForMotion}
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <div className={styles.container}>
        <div className={styles.topContainer}>
          <div className={styles.topLeftContainer}>
            <MarkButton
              isMarked={props.term.isMarked}
              onClick={handleOnClickMarkButton}
            />
            <SpeakButton
              fontSize="small"
              isPlaying={isPlaying}
              onClick={handleOnClickSpeakButton}
              title={speechShourtCut}
            />
          </div>
          <div className={styles.topRightContainer}>
            <PronunciationSelectView
              open={props.showingPronunciationSelectView}
              selectedPronunciation={props.englishPronunciation}
              onClickAction={(
                e: React.MouseEvent<HTMLDivElement>,
                pronunciation: EnglishPronunciation
              ) => {
                handleOnClickSpeakButton(e);
                props.onClickPronunciationSelectButton(pronunciation);
              }}
            />
          </div>
        </div>
        <p className={styles.titleText}>{t("LearningIncorrectViewTitle")}</p>
        <div
          className={styles.termContainer}
          lang={
            chineseCharacterMode === "japanese" ? "ja" : navigator?.language
          }
        >
          {props.userAnswer.trim().length !== 0 && (
            <div className={styles.userAnswerContainer}>
              <span className={styles.incorrectChip}>
                {t("IncorrectAnswer")}
              </span>
              <span className={styles.userAnswerText}>{props.userAnswer}</span>
            </div>
          )}
          {props.term.pronunciation.length !== 0 && (
            <p className={styles.pronunciationText}>
              {`[${props.term.pronunciation}]`}
            </p>
          )}
          <p className={styles.termText}>{props.term.term}</p>
          <p className={styles.definitionText}>{props.term.definition}</p>
          {props.term.memo.length !== 0 && (
            <p className={styles.memoText}>{props.term.memo}</p>
          )}
        </div>
        <div className={styles.inputContainer}>
          <div className={styles.inputRow}>
            <TextField
              ref={inputRef}
              className={classNames(styles.inputField, {
                [styles.inputFieldCompleted]: isCompleted,
              })}
              value={input}
              placeholder={t("LearningIncorrectViewHintMessage")}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              readOnly={isCompleted}
              errorText={
                isError ? t("LearningIncorrectViewIncorrectMessage") : undefined
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setInput(e.target.value);
                setIsError(false);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === "Enter") {
                  handleOnSubmit();
                }
              }}
            />
            <Button
              size="lg"
              className={styles.submitButton}
              disabled={input.trim().length === 0 || isCompleted}
              onClick={() => handleOnSubmit()}
            >
              {t("LearningIncorrectViewSubmitButton")}
            </Button>
          </div>
          <Button
            variant="ghost"
            fullWidth
            disabled={isCompleted}
            onClick={() => handleOnClickPassButton()}
          >
            {t("LearningIncorrectViewPassButtonTitle")}
          </Button>
        </div>
      </div>
      {audioSrc && (
        <AudioPlayer
          key={audioSrc}
          autoPlay={true}
          src={audioSrc}
          style={{ display: "none" }}
          onEnded={() => {
            setIsPlaying(false);
            setAudioSrc(undefined);
          }}
        />
      )}
    </motion.div>
  );
}
